import { cn } from '@/utils/cn';
import { useT } from '@/utils/i18n';
import { Menu } from '@/utils/webview-context-menu';
import { useCallback, useEffect, useState } from 'react';
import {
  downloadAttachmentSource,
  preparePreviewAsset,
  previewAttachmentSource,
} from './file-reference-actions';
import { useResolvedUrl } from './use-resolved-url';
import {
  isAgentFileShareRoute,
  requiresNativeURLResolution,
  resolveAttachmentDisplayURL,
  type AttachmentFileRef,
} from '@/utils/agent-file-url';
import { hasNativeJSBridge } from '@/utils/bridge-runtime';
import { ExclamationmarkTriangleFillSFSymbolMedium } from '@/assets/sf-symbols/medium/exclamationmark.triangle.fill';
import { DocumentFillSFSymbolMedium } from '@/assets/sf-symbols/medium/document.fill';
import type { AttachmentURLDiagnosticPayload } from './attachment-preview-diagnostics';

const maxEagerNativePreviewBytes = 50 * 1024 * 1024;

export interface FileAttachmentData {
  filename: string;
  contentType: string;
  path?: string;
  url?: string;
  environmentId?: string;
  sourceSessionId?: string;
  agentId?: string;
  signature?: string;
  fileRefs?: ReadonlyArray<AttachmentFileRef>;
  size: string; // Human-readable size string (e.g., "1.5 MB"), empty for directories
  sizeBytes?: number;
}

export type AttachmentUnavailableInfo = {
  description?: string;
  diagnostic?: AttachmentURLDiagnosticPayload;
  label: string;
  presentation?: 'error' | 'file';
  source?: string | null;
};

type AttachmentDetail = {
  label: string;
  title?: string;
  value: string;
};

/**
 * Request a file thumbnail from Swift native code via WebKit bridge
 * Uses QuickLook to generate a thumbnail for the file at the given path
 * Returns a base64 data URL of the file thumbnail
 */
async function requestFileThumbnail(path: string): Promise<string | null> {
  try {
    const result = await window.jsb?.MessagesBridge?.getFileIcon(path);
    return result ?? null;
  } catch {
    return null;
  }
}

export function formatAttachmentSize(sizeBytes?: number | null): string {
  if (
    typeof sizeBytes !== 'number' ||
    !Number.isFinite(sizeBytes) ||
    sizeBytes < 0
  ) {
    return '';
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
  }
  if (sizeBytes < 1024 * 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(sizeBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function isMediaContentType(contentType: string): boolean {
  const normalized = contentType.trim().toLowerCase();
  return normalized.startsWith('video/') || normalized.startsWith('audio/');
}

function isMediaFilename(filename: string): boolean {
  return ['mp4', 'm4v', 'mov', 'webm', 'mp3', 'm4a', 'wav', 'ogg'].includes(
    getFileExtension(filename)
  );
}

export function describeFileReferenceEnvironment(
  environmentId?: string | null
): string | null {
  const trimmed = environmentId?.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.replaceAll('_', '-').toLowerCase();
  if (normalized === 'vfs' || normalized === 'agent-vfs') {
    return 'agent workspace';
  }
  if (normalized === 'local-vm' || normalized.startsWith('local-vm-')) {
    return 'a safe workspace on this Mac';
  }
  if (normalized === 'cloud-vm') {
    return 'a cloud workspace';
  }
  if (normalized === 'local' || normalized.startsWith('local-')) {
    return 'this Mac';
  }

  return trimmed;
}

export function isWebInaccessibleFileReference({
  agentId,
  environmentId,
  fileRefs,
  nativeBridgeAvailable,
  path,
  signature,
  url,
}: {
  agentId?: string;
  environmentId?: string;
  fileRefs?: ReadonlyArray<AttachmentFileRef> | null;
  nativeBridgeAvailable: boolean;
  path?: string;
  signature?: string;
  url?: string;
}): boolean {
  if (!path?.trim() || nativeBridgeAvailable) {
    return false;
  }

  return (
    resolveAttachmentDisplayURL({
      src: url,
      filePath: path,
      environmentId,
      agentId,
      signature,
      fileRefs,
    }) === null
  );
}

export function buildFileAccessRequestMessage({
  environmentId,
  environmentLabel,
  filename,
  path,
}: {
  environmentId?: string;
  environmentLabel?: string | null;
  filename: string;
  path: string;
}): string {
  const environmentLine =
    environmentLabel && environmentId && environmentLabel !== environmentId
      ? `${environmentLabel} (${environmentId})`
      : (environmentLabel ?? environmentId ?? 'unknown environment');

  return [
    'Please provide a complete, accessible version of this file attachment.',
    '',
    `File: ${filename}`,
    `Location: ${path}`,
    `Environment: ${environmentLine}`,
    '',
    'The current Bridge web view only has this incomplete file reference and cannot access the file. Please either attach/export a downloadable copy, or provide the complete information needed to access it here.',
  ].join('\n');
}

function shouldDeferNativePreviewResolution(params: {
  source: string | null;
  contentType: string;
  filename: string;
  sizeBytes?: number;
}): boolean {
  if (!hasNativeJSBridge() || !params.source) {
    return false;
  }
  if (!requiresNativeURLResolution(params.source)) {
    return false;
  }

  return (
    isMediaContentType(params.contentType) ||
    isMediaFilename(params.filename) ||
    (typeof params.sizeBytes === 'number' &&
      params.sizeBytes > maxEagerNativePreviewBytes)
  );
}

function getFileExtension(filename: string): string {
  const index = filename.lastIndexOf('.');
  if (index < 0 || index === filename.length - 1) {
    return '';
  }
  return filename.slice(index + 1).toLowerCase();
}

function getFriendlyFileType(filename: string, contentType: string): string {
  const ext = getFileExtension(filename);

  switch (ext) {
    case 'md':
    case 'markdown':
    case 'mdown':
    case 'mkd':
      return 'Markdown document';
    case 'pdf':
      return 'PDF document';
    case 'txt':
    case 'log':
      return 'Text document';
    case 'json':
      return 'JSON file';
    case 'png':
      return 'PNG image';
    case 'jpg':
    case 'jpeg':
      return 'JPEG image';
    case 'gif':
      return 'GIF image';
    case 'webp':
      return 'WebP image';
    case 'mp4':
      return 'MP4 video';
    case 'mov':
      return 'QuickTime video';
    case 'mp3':
      return 'MP3 audio';
    case 'wav':
      return 'WAV audio';
  }

  switch (contentType) {
    case 'application/pdf':
      return 'PDF document';
    case 'text/plain':
      return 'Text document';
    case 'application/json':
      return 'JSON file';
    case 'image/png':
      return 'PNG image';
    case 'image/jpeg':
      return 'JPEG image';
    case 'image/gif':
      return 'GIF image';
    case 'image/webp':
      return 'WebP image';
    case 'video/mp4':
      return 'MP4 video';
    case 'audio/mpeg':
      return 'MP3 audio';
  }

  if (!contentType || contentType === 'application/octet-stream') {
    return ext ? `${ext.toUpperCase()} file` : 'File';
  }

  return contentType;
}

function webInaccessibleDescription(environmentLabel: string | null): string {
  if (environmentLabel) {
    return `The preview source is in ${environmentLabel}, and this web page cannot access that environment.`;
  }

  return 'The message has a file path but no preview URL this web page can open.';
}

function isAttachmentDetail(
  detail: AttachmentDetail | null
): detail is AttachmentDetail {
  return Boolean(detail?.value);
}

/**
 * Hook to fetch file thumbnail from Swift bridge using QuickLook
 */
function useFileThumbnail(path?: string): string | null {
  const [iconUrl, setIconUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setIconUrl(null);
      return;
    }

    let cancelled = false;

    requestFileThumbnail(path).then(url => {
      if (!cancelled) {
        setIconUrl(url);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [path]);

  return iconUrl;
}

/**
 * File attachment card component
 */
export const FileAttachmentCard = ({
  filename,
  contentType,
  path,
  url,
  environmentId,
  sourceSessionId,
  agentId,
  signature,
  fileRefs,
  size,
  sizeBytes,
  unavailable,
  showSourceMetadata = true,
  className,
  onRequestFileAccess,
  ...props
}: {
  filename: string;
  contentType: string;
  path?: string;
  url?: string;
  environmentId?: string;
  sourceSessionId?: string;
  agentId?: string;
  signature?: string;
  fileRefs?: ReadonlyArray<AttachmentFileRef> | null;
  size?: string;
  sizeBytes?: number;
  unavailable?: AttachmentUnavailableInfo | null;
  showSourceMetadata?: boolean;
  className?: string;
  onRequestFileAccess?: (message: string) => void | Promise<void>;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const t = useT();
  const sourcePath = path?.trim() || undefined;
  const sourceUrl = url?.trim() || undefined;
  const nativeBridgeAvailable = hasNativeJSBridge();
  const [accessRequestState, setAccessRequestState] = useState<
    'idle' | 'sending' | 'sent'
  >('idle');
  const iconUrl = useFileThumbnail(sourcePath);
  const displaySource = resolveAttachmentDisplayURL({
    src: sourceUrl,
    filePath: sourcePath,
    environmentId,
    agentId,
    signature,
    fileRefs,
  });
  const deferPreviewResolution = shouldDeferNativePreviewResolution({
    source: displaySource,
    contentType,
    filename,
    sizeBytes,
  });
  const resolvedUrl = useResolvedUrl(
    deferPreviewResolution
      ? {}
      : {
          src: sourceUrl,
          filePath: sourcePath,
          environmentId,
          agentId,
          signature,
          fileRefs,
        }
  );
  const fileType = getFriendlyFileType(filename, contentType);
  const displaySize = size || formatAttachmentSize(sizeBytes);
  const previewSource = resolvedUrl ?? displaySource;
  const isSignedFileShareSource = Boolean(
    (resolvedUrl && isAgentFileShareRoute(resolvedUrl)) ||
    (displaySource && isAgentFileShareRoute(displaySource))
  );
  const fallbackPath = isSignedFileShareSource ? undefined : sourcePath;
  const canPreview = Boolean(
    previewSource || (sourcePath && nativeBridgeAvailable)
  );
  const isUnavailableInWeb = isWebInaccessibleFileReference({
    agentId,
    environmentId,
    fileRefs,
    nativeBridgeAvailable,
    path: sourcePath,
    signature,
    url: sourceUrl,
  });
  const hasUnavailableState = Boolean(unavailable) || isUnavailableInWeb;
  const canOpenPreview = canPreview && !hasUnavailableState;
  const nativeAttachmentDownloadAvailable = Boolean(
    nativeBridgeAvailable && window.jsb?.MessagesBridge?.saveAttachment
  );
  const requiresNativeDownloadResolution = Boolean(
    previewSource &&
    nativeBridgeAvailable &&
    requiresNativeURLResolution(previewSource) &&
    !nativeAttachmentDownloadAvailable
  );
  const canDownload = Boolean(
    previewSource && !hasUnavailableState && !requiresNativeDownloadResolution
  );
  const canOpen = !canDownload && canOpenPreview;
  const shouldDownloadOnCardClick = canDownload && !nativeBridgeAvailable;
  const canClickCard = canOpenPreview || shouldDownloadOnCardClick;
  const environmentLabel = describeFileReferenceEnvironment(environmentId);
  const nativeSendMessage =
    typeof window === 'undefined'
      ? undefined
      : window.jsb?.MessagesBridge?.sendMessage;
  const canRequestFileAccess = Boolean(
    onRequestFileAccess || nativeSendMessage
  );
  const unavailableTitle =
    unavailable?.label ??
    (isUnavailableInWeb ? 'File preview unavailable' : 'Preview unavailable');
  const unavailableDescription =
    unavailable?.description ??
    (isUnavailableInWeb
      ? webInaccessibleDescription(environmentLabel)
      : 'The message did not include enough information to build a preview.');
  const diagnosticDetails: AttachmentDetail[] = (
    [
      sourcePath
        ? {
            label: 'Location',
            title: sourcePath,
            value: sourcePath,
          }
        : null,
      environmentLabel
        ? {
            label: 'Environment',
            title: environmentId,
            value: environmentLabel,
          }
        : null,
      unavailable?.diagnostic?.status
        ? {
            label: 'Status',
            value: String(unavailable.diagnostic.status),
          }
        : null,
      unavailable?.diagnostic?.errorCode
        ? {
            label: 'Code',
            value: unavailable.diagnostic.errorCode,
          }
        : null,
      unavailable?.diagnostic?.message
        ? {
            label: 'Message',
            title: unavailable.diagnostic.message,
            value: unavailable.diagnostic.message,
          }
        : null,
      unavailable?.source
        ? {
            label: 'Source',
            title: unavailable.source,
            value: unavailable.source,
          }
        : null,
    ] as Array<AttachmentDetail | null>
  ).filter(isAttachmentDetail);
  const inlineSourceMetadata: AttachmentDetail[] = (
    [
      environmentLabel
        ? {
            label: 'Environment',
            title: environmentId,
            value: environmentLabel,
          }
        : null,
      sourcePath
        ? {
            label: 'Location',
            title: sourcePath,
            value: sourcePath,
          }
        : null,
    ] as Array<AttachmentDetail | null>
  ).filter(isAttachmentDetail);

  useEffect(() => {
    if (!resolvedUrl) {
      return;
    }
    preparePreviewAsset(resolvedUrl, filename, contentType, sizeBytes);
  }, [contentType, filename, resolvedUrl, sizeBytes]);

  const downloadAttachment = useCallback(() => {
    if (!previewSource) {
      return;
    }

    void downloadAttachmentSource(previewSource, {
      fileName: filename,
      mimeType: contentType,
      sizeBytes,
      fallbackPath,
      environmentId,
      sourceSessionId,
    });
  }, [
    contentType,
    environmentId,
    fallbackPath,
    filename,
    previewSource,
    sizeBytes,
    sourceSessionId,
  ]);

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!canOpenPreview && !canDownload) {
        return;
      }

      let menu = Menu.create();
      if (canOpenPreview) {
        menu = menu.pushItem({
          title: t('Preview'),
          icon: iconUrl
            ? Menu.icon.dataUrl(iconUrl)
            : Menu.icon.symbol('document'),
          onClick: () => {
            void previewAttachmentSource(previewSource, {
              fileName: filename,
              mimeType: contentType,
              sizeBytes,
              fallbackPath,
              environmentId,
              sourceSessionId,
            });
          },
        });
      }
      if (canDownload) {
        menu = menu.pushItem({
          title: t('Download'),
          icon: Menu.icon.symbol('square.and.arrow.down'),
          onClick: downloadAttachment,
        });
      }

      menu.popup(event);
    },
    [
      canDownload,
      canOpenPreview,
      contentType,
      downloadAttachment,
      environmentId,
      fallbackPath,
      filename,
      iconUrl,
      previewSource,
      sizeBytes,
      sourceSessionId,
      t,
    ]
  );

  const handlePreview = useCallback(() => {
    if (!canOpenPreview) {
      return;
    }

    void previewAttachmentSource(previewSource, {
      fileName: filename,
      mimeType: contentType,
      sizeBytes,
      fallbackPath,
      environmentId,
      sourceSessionId,
    });
  }, [
    canOpenPreview,
    contentType,
    environmentId,
    fallbackPath,
    filename,
    previewSource,
    sizeBytes,
    sourceSessionId,
  ]);

  const handleCardClick = useCallback(() => {
    if (shouldDownloadOnCardClick) {
      downloadAttachment();
      return;
    }

    handlePreview();
  }, [downloadAttachment, handlePreview, shouldDownloadOnCardClick]);

  const handleDownloadClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      downloadAttachment();
    },
    [downloadAttachment]
  );

  const handleOpenClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handlePreview();
    },
    [handlePreview]
  );

  const handleRequestAccess = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!sourcePath || accessRequestState !== 'idle') {
        return;
      }

      const message = buildFileAccessRequestMessage({
        environmentId,
        environmentLabel,
        filename,
        path: sourcePath,
      });

      setAccessRequestState('sending');
      try {
        if (onRequestFileAccess) {
          await onRequestFileAccess(message);
        } else {
          await nativeSendMessage?.(message);
        }
        setAccessRequestState('sent');
      } catch (error) {
        console.error('Failed to request accessible file:', error);
        setAccessRequestState('idle');
      }
    },
    [
      accessRequestState,
      environmentId,
      environmentLabel,
      filename,
      nativeSendMessage,
      onRequestFileAccess,
      sourcePath,
    ]
  );

  const stopCardClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <div
      onContextMenu={handleContextMenu}
      onClick={canClickCard ? handleCardClick : undefined}
      className={cn(
        'p-3 min-w-[40%] max-w-full w-full text-left',
        'flex items-center gap-3',
        'rounded-lg border',
        'transition-colors',
        hasUnavailableState
          ? 'border-warning-fg/20 bg-warning-bg'
          : 'border-border bg-surface-card',
        canClickCard
          ? 'cursor-pointer hover:bg-fill-soft active:bg-fill-medium'
          : 'cursor-default',
        className
      )}
      {...props}
    >
      <div className="shrink-0 w-8 h-8 flex items-center justify-center">
        {hasUnavailableState ? (
          <ExclamationmarkTriangleFillSFSymbolMedium
            className="text-xl text-warning-fg"
            aria-hidden="true"
          />
        ) : iconUrl ? (
          <img src={iconUrl} alt="" className="w-8 h-8 object-contain" />
        ) : (
          <DocumentFillSFSymbolMedium
            className="text-[1.375rem] text-text-secondary"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="font-medium text-sm truncate">{filename}</div>
        <div className="text-xs text-text-secondary whitespace-nowrap">
          {fileType} {displaySize && `• ${displaySize}`}
        </div>
        {showSourceMetadata &&
          !hasUnavailableState &&
          inlineSourceMetadata.length > 0 && (
            <div className="mt-1 space-y-1 text-[0.6875rem] leading-4 text-text-secondary">
              {inlineSourceMetadata.map(detail => (
                <div
                  key={`${detail.label}:${detail.value}`}
                  className="truncate"
                  title={detail.title ?? detail.value}
                >
                  {detail.label}: {detail.value}
                </div>
              ))}
            </div>
          )}
        {!hasUnavailableState && (canDownload || canOpen) ? (
          <div className="mt-2 flex items-center gap-2">
            {canDownload ? (
              <button
                type="button"
                onClick={handleDownloadClick}
                className={cn(
                  'inline-flex h-6 items-center rounded-md border border-border px-2',
                  'text-[0.6875rem] font-medium leading-none text-text-secondary',
                  'transition-colors hover:bg-fill-soft hover:text-text-primary active:bg-fill-medium'
                )}
              >
                {t('Download')}
              </button>
            ) : null}
            {canOpen ? (
              <button
                type="button"
                onClick={handleOpenClick}
                className={cn(
                  'inline-flex h-6 items-center rounded-md border border-border px-2',
                  'text-[0.6875rem] font-medium leading-none text-text-secondary',
                  'transition-colors hover:bg-fill-soft hover:text-text-primary active:bg-fill-medium'
                )}
              >
                {t('Open')}
              </button>
            ) : null}
          </div>
        ) : null}
        {hasUnavailableState && (
          <div className="mt-1 space-y-1 text-[0.6875rem] leading-4">
            <div className="font-medium text-warning-fg">
              {unavailableTitle}
            </div>
            <div className="text-text-secondary">{unavailableDescription}</div>
            <div className="text-text-tertiary">
              {canRequestFileAccess
                ? 'Open Bridge in that environment, or ask the agent to provide a downloadable copy.'
                : 'Open Bridge in that environment to access it.'}
            </div>
            {diagnosticDetails.length > 0 ? (
              <details
                className="group text-text-secondary"
                onClick={stopCardClick}
              >
                <summary className="inline cursor-pointer select-none text-text-tertiary transition-colors hover:text-text-secondary">
                  Preview details
                </summary>
                <div className="mt-1 space-y-1">
                  {diagnosticDetails.map(detail => (
                    <div
                      key={`${detail.label}:${detail.value}`}
                      className="truncate"
                      title={detail.title ?? detail.value}
                    >
                      {detail.label}: {detail.value}
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
            {canRequestFileAccess ? (
              <button
                type="button"
                onClick={handleRequestAccess}
                disabled={accessRequestState !== 'idle'}
                className={cn(
                  'mt-1 inline-flex h-6 items-center rounded-md border border-warning-fg/25 px-2',
                  'text-[0.6875rem] font-medium leading-none text-warning-fg',
                  'transition-colors hover:bg-warning-fg/10 active:bg-warning-fg/15',
                  'disabled:cursor-default disabled:opacity-70',
                  accessRequestState === 'sent' &&
                    'border-border bg-fill-soft text-text-tertiary hover:bg-fill-soft active:bg-fill-soft'
                )}
              >
                <span role="status" aria-live="polite">
                  {accessRequestState === 'sent'
                    ? 'Request sent'
                    : accessRequestState === 'sending'
                      ? 'Sending request...'
                      : 'Ask agent to make accessible'}
                </span>
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
